import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type { Invitation, PersonDetail, Role, TemporaryPersonCreated } from '@up2d8/contracts';
import { localized } from '../../shared/format';
import { invitationToast } from '../../shared/invitation-toast';
import { API_BASE, useSession } from '../../shared/session';
import {
  AdminIcon,
  Btn,
  CodeDisplay,
  DefinitionList,
  Field,
  Input,
  Modal,
  Note,
  Radio,
  Row,
  Segmented,
  Select,
} from '../admin-kit';
import { useMelding } from './people-ui';
import { useTemporaryForm } from './temporary-form';

/**
 * De drie vensters van Medewerkers (BHR-2, M3 t/m M6): iemand toevoegen, een account aanmaken, en
 * een tijdelijke kracht met zijn code. Korte vormen, dus vensters en geen paneel (spec A §6).
 */

type Taal = 'nl' | 'fr' | 'en';

/** De talen in hun eigen taal (deviations §7), als segment zoals in M3. */
const TALEN: { value: Taal; label: string }[] = [
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
];

/** M3 — Medewerker toevoegen. */
export function NewPersonModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (person: PersonDetail) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState<Taal | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
    De taal begint op de standaardtaal van het bedrijf (punt 16).

    Ze stond vast op `nl`. Bij een Franstalig bedrijf betekende dat: elke nieuwe medewerker kreeg
    een Nederlandse app tot iemand het per persoon rechtzette - en dat merkt die persoon zelf als
    eerste, op zijn eerste werkdag.
  */
  const tenant = useQuery({
    queryKey: ['tenant', 'self'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/tenant');
      return (data as { defaultLanguage?: string } | undefined) ?? null;
    },
  });
  const gekozenTaal = language ?? (tenant.data?.defaultLanguage as Taal | undefined) ?? 'nl';

  const create = useMutation({
    mutationFn: async () => {
      const { data, error: failure } = await api.POST('/api/v1/people', {
        body: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          employeeNumber: employeeNumber.trim() || null,
          // Leeg blijft leeg: een e-mailadres is optioneel, en een lege string zou de server als
          // een ongeldig adres lezen.
          email: email.trim() || null,
          preferredLanguage: gekozenTaal,
        } as never,
      });
      if (failure || !data) throw failure ?? new Error('Aanmaken mislukt');
      return data as PersonDetail;
    },
    onSuccess: (person) => {
      onCreated(person);
      onClose();
    },
    onError: () => setError(t('people.createFailed')),
  });

  // Leegmaken bij het sluiten (punt 15), en niet alleen na een gelukte poging: wie het venster
  // afbreekt en opnieuw opent, hoort een leeg formulier te zien.
  useEffect(() => {
    if (open) return;
    setFirstName('');
    setLastName('');
    setEmployeeNumber('');
    setEmail('');
    setLanguage(null);
    setError(null);
  }, [open]);

  const klaar = Boolean(firstName.trim() && lastName.trim());

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('people.new')}
      testId="new-person-dialog"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            type="submit"
            form="medewerker-toevoegen"
            icon={<AdminIcon name="plus" />}
            disabled={!klaar || create.isPending}
            aria-busy={create.isPending}
          >
            {t('people.new')}
          </Btn>
        </>
      }
    >
      <form
        id="medewerker-toevoegen"
        className="a-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (klaar) create.mutate();
        }}
      >
        <Note tone="info">{t('beheer.medewerkers.newNote')}</Note>
        <Row>
          <Field label={t('people.firstName')}>
            {(props) => (
              <Input
                {...props}
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            )}
          </Field>
          <Field label={t('people.lastName')}>
            {(props) => (
              <Input
                {...props}
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            )}
          </Field>
        </Row>
        <Field
          label={t('people.employeeNumber')}
          hint={t('beheer.medewerkers.employeeNumberHint')}
          error={error ?? undefined}
        >
          {(props) => (
            <Input
              {...props}
              value={employeeNumber}
              onChange={(event) => setEmployeeNumber(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('people.email')} optional hint={t('beheer.medewerkers.emailHint')}>
          {(props) => (
            <Input
              {...props}
              type="email"
              placeholder={t('beheer.medewerkers.emailPlaceholder')}
              data-testid="new-person-email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <div className="a-field">
          <div className="a-label" id="nieuwe-medewerker-taal">
            {t('people.language')}
          </div>
          <Segmented
            labelledBy="nieuwe-medewerker-taal"
            options={TALEN.map((taal) => ({
              ...taal,
              testId: `new-person-language-${taal.value}`,
            }))}
            value={gekozenTaal}
            onChange={setLanguage}
          />
        </div>
      </form>
    </Modal>
  );
}

/**
 * M4 — Account aanmaken (ACC-2): een account met naam, e-mail en rol, en de mail vertrekt meteen.
 *
 * Dezelfde aanvraag als "Beheerder uitnodigen" op het scherm Uitnodigingen (`InviteDialog`), in de
 * vorm van M4. De uitnodiging kent **één** rol (`roleId`); de mockup tekent vinkjes, hier zijn het
 * keuzerondjes (deviations.md, BHR-2). Leeg is de rol Beheerder, zoals daar.
 */
export function AccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [fout, setFout] = useState<string | null>(null);
  // Na het sluiten van M4, rechtsonder: het venster is dan weg, de melding blijft.
  const melding = useMelding();

  useEffect(() => {
    if (open) return;
    setEmail('');
    setDisplayName('');
    setRoleId('');
    setFout(null);
  }, [open]);

  /*
    De keuzes tonen alleen rollen die déze beheerder mag toekennen.

    De server weigert de rest ook (een rol met meer rechten dan je zelf hebt is `403`), maar een
    lijst met opties die altijd falen, is een lijst die mensen leert dat het systeem willekeurig
    is. Zonder `access.role.assign` staan er geen keuzes: dan wordt het Beheerder.
  */
  const roles = useQuery({
    queryKey: ['access', 'roles'],
    enabled: open && has('access.role.assign'),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/roles');
      return (data as { roles: Role[] } | undefined)?.roles ?? [];
    },
  });

  const invite = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/invitations', {
        body: {
          email: email.trim(),
          displayName: displayName.trim(),
          roleHint: 'admin' as const,
          roleId: roleId || null,
        } as never,
      });
      if (error) throw error;
      return data as Invitation | undefined;
    },
    onSuccess: async (invitation) => {
      onClose();
      // Wat er echt gebeurde: de uitnodiging kan bestaan zonder dat de mail aankwam.
      const uitkomst = invitationToast(invitation);
      if (uitkomst.failed) melding.fout(t(uitkomst.key));
      else melding.ok(t(uitkomst.key));
      await queryClient.invalidateQueries({ queryKey: ['identity', 'invitations'] });
    },
    // Elke code een eigen zin: "er ging iets mis" laat de beheerder raden of hij het adres
    // verkeerd typte, of dat die persoon er allang is.
    onError: (error) => {
      switch (problemCode(error)) {
        case 'ALREADY_MEMBER':
          setFout(t('invitations.alreadyMember'));
          break;
        case 'ALREADY_INVITED':
          setFout(t('invitations.alreadyInvited'));
          break;
        case 'FORBIDDEN':
        case 'POLICY_DENIED':
          setFout(t('invitations.roleTooBig'));
          break;
        case 'RATE_LIMITED':
          setFout(t('invitations.rateLimited'));
          break;
        default:
          setFout(t('errors.generic'));
      }
    },
  });

  // De korte uitleg uit M4 bij de rollen waar ze bestaat; de andere rollen spreken voor zich.
  const uitleg: Record<string, string> = {
    admin: t('beheer.medewerkers.roleAdminHint'),
    communication: t('beheer.medewerkers.roleCommunicationHint'),
  };
  const klaar = email.trim() !== '' && displayName.trim().length >= 2;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t('people.createAccount')}
        sub={t('beheer.medewerkers.accountSub')}
        testId="account-dialog"
        footer={
          <>
            <Btn variant="sec" onClick={onClose}>
              {t('common.cancel')}
            </Btn>
            <Btn
              type="submit"
              form="account-aanmaken"
              icon={<AdminIcon name="send" />}
              testId="send-invite"
              disabled={!klaar || invite.isPending}
              aria-busy={invite.isPending}
            >
              {t('invitations.send')}
            </Btn>
          </>
        }
      >
        <form
          id="account-aanmaken"
          className="a-form"
          onSubmit={(event) => {
            event.preventDefault();
            setFout(null);
            if (klaar) invite.mutate();
          }}
        >
          <Field label={t('people.name')}>
            {(props) => (
              <Input
                {...props}
                data-testid="invite-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            )}
          </Field>
          <Field label={t('people.email')}>
            {(props) => (
              <Input
                {...props}
                type="email"
                data-testid="invite-email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          {has('access.role.assign') ? (
            <fieldset className="a-field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="a-label">{t('roles.title')}</legend>
              <Row>
                <Radio
                  name="account-rol"
                  value=""
                  checked={roleId === ''}
                  onChange={() => setRoleId('')}
                  label={t('invitations.roleAdmin')}
                  hint={uitleg.admin}
                />
                {(roles.data ?? [])
                  .filter((role) => role.key !== 'admin')
                  .map((role) => (
                    <Radio
                      key={role.id}
                      name="account-rol"
                      value={role.id}
                      checked={roleId === role.id}
                      onChange={() => setRoleId(role.id)}
                      label={localized(role.name as Record<string, string>) ?? role.id}
                      hint={role.key ? uitleg[role.key] : undefined}
                    />
                  ))}
              </Row>
              <div className="a-hint">{t('beheer.medewerkers.roleHint')}</div>
            </fieldset>
          ) : null}
          {fout ? (
            <Note tone="danger" role="alert" testId="invite-error">
              {fout}
            </Note>
          ) : null}
        </form>
      </Modal>
      {melding.element}
    </>
  );
}

/**
 * M5 en M6 — Tijdelijke kracht toevoegen, en daarna de code.
 *
 * Dezelfde aanvraag en controle als de teamleider in de app (`useTemporaryForm`); dit is de vorm
 * van het beheer. Het venster wordt pas gemaakt als het opengaat, zodat het elke keer leeg begint.
 */
export function TemporaryModal({
  open,
  onClose,
  groups,
}: {
  open: boolean;
  onClose: () => void;
  groups: { id: string; name: string }[];
}) {
  if (!open) return null;
  return <TemporaryModalInhoud onClose={onClose} groups={groups} />;
}

function TemporaryModalInhoud({
  onClose,
  groups,
}: {
  onClose: () => void;
  groups: { id: string; name: string }[];
}) {
  const { t } = useTranslation();
  const form = useTemporaryForm({ groups });

  if (form.result) {
    return (
      <TemporaryCode
        result={form.result}
        groupName={groups.find((group) => group.id === form.groupId)?.name ?? null}
        onAnother={form.another}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('temporary.add')}
      testId="temporary-form"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            type="submit"
            form="tijdelijke-kracht"
            icon={<AdminIcon name="qr" />}
            disabled={form.pending}
            aria-busy={form.pending}
            testId="temporary-submit"
          >
            {t('temporary.submit')}
          </Btn>
        </>
      }
    >
      <form
        id="tijdelijke-kracht"
        className="a-form"
        onSubmit={(event) => {
          event.preventDefault();
          form.submit();
        }}
      >
        <Row>
          <Field label={t('temporary.firstName')} error={form.errors.firstName}>
            {(props) => (
              <Input
                {...props}
                value={form.firstName}
                autoComplete="off"
                onChange={(event) => form.setFirstName(event.target.value)}
                data-testid="temporary-first-name"
              />
            )}
          </Field>
          <Field label={t('temporary.lastName')} error={form.errors.lastName}>
            {(props) => (
              <Input
                {...props}
                value={form.lastName}
                autoComplete="off"
                onChange={(event) => form.setLastName(event.target.value)}
                data-testid="temporary-last-name"
              />
            )}
          </Field>
        </Row>
        <div className="a-field">
          <div className="a-label" id="tijdelijke-soort">
            {t('temporary.type')}
          </div>
          <Segmented
            labelledBy="tijdelijke-soort"
            options={[
              { value: 'temporary' as const, label: t('temporary.typeTemporary') },
              { value: 'contractor' as const, label: t('temporary.typeContractor') },
            ]}
            value={form.personType}
            onChange={form.setPersonType}
          />
        </div>
        <Field label={t('temporary.originCompany')} optional error={form.errors.originCompany}>
          {(props) => (
            <Input
              {...props}
              value={form.originCompany}
              maxLength={120}
              onChange={(event) => form.setOriginCompany(event.target.value)}
              data-testid="temporary-origin"
            />
          )}
        </Field>
        <Row>
          <Field
            label={t('temporary.endDate')}
            hint={t('beheer.medewerkers.endDateHint')}
            error={form.errors.endDate}
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                min={form.minEndDate}
                value={form.endDate}
                onChange={(event) => form.setEndDate(event.target.value)}
                data-testid="temporary-end-date"
              />
            )}
          </Field>
          {groups.length > 0 ? (
            <Field label={t('temporary.group')} optional>
              {(props) => (
                <Select
                  {...props}
                  value={form.groupId}
                  onChange={(event) => form.setGroupId(event.target.value)}
                  data-testid="temporary-group"
                >
                  <option value="">{t('temporary.noGroup')}</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
        </Row>
        {form.errors.form ? (
          <Note tone="danger" role="alert">
            {form.errors.form}
          </Note>
        ) : null}
      </form>
    </Modal>
  );
}

/** M6 — de code, groot en afdrukbaar, met wanneer het account stopt. */
function TemporaryCode({
  result,
  groupName,
  onAnother,
  onClose,
}: {
  result: TemporaryPersonCreated;
  groupName: string | null;
  onAnother: () => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [qr, setQr] = useState(false);
  const naam = `${result.person.firstName} ${result.person.lastName}`;
  const einde = result.person.plannedExitDate
    ? new Date(`${result.person.plannedExitDate}T12:00:00`).toLocaleDateString(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;
  const geldig = new Date(result.activationExpiresAt).toLocaleDateString(i18n.language, {
    day: 'numeric',
    month: 'long',
  });

  const gegevens: [string, string][] = [];
  if (result.person.originCompany) {
    gegevens.push([
      result.person.personType === 'contractor'
        ? t('beheer.medewerkers.company')
        : t('beheer.medewerkers.agency'),
      result.person.originCompany,
    ]);
  }
  if (einde)
    gegevens.push([
      t('beheer.medewerkers.accountStops'),
      t('beheer.medewerkers.after', { date: einde }),
    ]);
  if (groupName) gegevens.push([t('temporary.group'), groupName]);

  return (
    <Modal
      open
      onClose={onClose}
      title={t('beheer.medewerkers.addedTitle', { name: naam })}
      testId="temporary-result"
      footer={
        <>
          <Btn variant="ghost" onClick={onAnother}>
            {t('temporary.another')}
          </Btn>
          <Btn variant="sec" icon={<AdminIcon name="printer" />} onClick={() => window.print()}>
            {t('access.print')}
          </Btn>
          <Btn onClick={onClose}>{t('beheer.medewerkers.done')}</Btn>
        </>
      }
    >
      <p>
        {t('beheer.medewerkers.codeIntro', { name: result.person.firstName })}{' '}
        <span className="a-mono" data-testid="temporary-number">
          {result.employeeNumber}
        </span>
        .
      </p>
      <CodeDisplay
        code={result.activationCode}
        label={t('beheer.medewerkers.codeLabel')}
        testId="temporary-code"
      />
      <div className="a-hint">{t('temporary.codeValid', { date: geldig })}</div>
      {gegevens.length > 0 ? <DefinitionList items={gegevens} /> : null}
      {/* Niet in M6, wel in de app: de QR-code voor wie liever scant dan typt. */}
      {result.invitationCode ? (
        qr ? (
          <div style={{ textAlign: 'center' }} data-testid="temporary-qr">
            <img
              src={`${API_BASE}/api/v1/public/invitations/${encodeURIComponent(result.invitationCode)}/qr`}
              alt={t('temporary.qrAlt')}
              style={{ width: 224, height: 224, margin: '0 auto' }}
            />
            <div className="a-hint">{t('temporary.qrHint')}</div>
          </div>
        ) : (
          <div>
            <Btn
              variant="sec"
              size="sm"
              icon={<AdminIcon name="qr" />}
              onClick={() => setQr(true)}
              testId="temporary-show-qr"
            >
              {t('temporary.showQr')}
            </Btn>
          </div>
        )
      ) : null}
    </Modal>
  );
}
